import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { ogrnLabel } from '../utils/ogrn'

import { listOperatorSuppliers, type OperatorSuppliersPageDto } from '../api/operator'
import { operatorSupplierCatalogPath, operatorSupplierPath } from '../operator/paths'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { formatCityLabel } from '../utils/cityLabel'

export const OperatorSuppliersScreen = () => {
  const [pageIndex, setPageIndex] = useState(0)
  const [page, setPage] = useState<OperatorSuppliersPageDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')

  const loadPage = useCallback(async (index: number, query?: string) => {
    setLoading(true)
    setError(null)
    try {
      const result = await listOperatorSuppliers(index, 50, query)
      setPage(result)
      setPageIndex(index)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить поставщиков')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadPage(0, search)
  }, [loadPage, search])

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault()
    setSearch(searchInput.trim())
  }

  const supplierLabel = (supplier: { trade_name: string | null; legal_name: string }) =>
    supplier.trade_name?.trim() || supplier.legal_name

  return (
    <section className="market-page">
      <MarketPageHeader
        title="Поставщики"
        description="Организации-поставщики для кабинета оператора"
      />

      <form className="market-toolbar market-toolbar--controls" onSubmit={handleSearchSubmit}>
        <label className="echat-field market-toolbar__field">
          <span className="visually-hidden">Поиск</span>
          <input
            className="echat-field__input"
            type="search"
            placeholder="Название, ОГРН или город"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>
        <button type="submit" className="echat-btn echat-btn--ghost">
          Найти
        </button>
      </form>

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && page?.content.length === 0 ? (
        <MarketEmptyState
          title="Поставщики не найдены"
          description={
            search
              ? 'Попробуйте изменить запрос — ищем по названию, ОГРН или городу.'
              : 'Когда поставщики зарегистрируются в системе, они появятся в этом списке.'
          }
        />
      ) : null}

      {page && page.content.length > 0 ? (
        <div className="market-table-wrap">
          <table className="market-table">
            <thead>
              <tr>
                <th>Поставщик</th>
                <th>Юр. название</th>
                <th>Регистрационный номер</th>
                <th>Город</th>
                <th>Статус</th>
                <th aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {page.content.map((supplier) => (
                <tr key={supplier.id}>
                  <td className="market-table__cell--truncate">{supplierLabel(supplier)}</td>
                  <td className="market-table__cell--truncate">{supplier.legal_name}</td>
                  <td>
                    {supplier.ogrn ? (
                      <>
                        <span className="market-table__hint">{ogrnLabel(supplier.ogrn)}</span> {supplier.ogrn}
                      </>
                    ) : (
                      // Карточки, заведённые до перехода на ОГРН, хранят только ИНН. Показываем
                      // его как есть: пустая ячейка выглядела бы как потерянные данные.
                      supplier.inn ?? '—'
                    )}
                  </td>
                  <td>{formatCityLabel(supplier.city)}</td>
                  <td className="market-table__cell--status">
                    {supplier.needs_attention > 0 ? (
                      <span className="echat-badge echat-badge--warning">
                        Проверить: {supplier.needs_attention}
                      </span>
                    ) : null}
                    {supplier.active ? (
                      <span className="echat-badge echat-badge--success">Активен</span>
                    ) : (
                      <span className="echat-badge echat-badge--muted">Неактивен</span>
                    )}
                  </td>
                  <td>
                    <div className="market-inline-actions">
                      <Link
                        className="echat-btn echat-btn--ghost echat-btn--sm"
                        to={operatorSupplierPath(supplier.id)}
                      >
                        Карточка
                      </Link>
                      <Link
                        className="echat-btn echat-btn--ghost echat-btn--sm"
                        to={operatorSupplierCatalogPath(supplier.id)}
                      >
                        Каталог
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {page && page.totalPages > 1 ? (
        <div className="market-pagination">
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex <= 0 || loading}
            onClick={() => void loadPage(pageIndex - 1, search)}
          >
            ←
          </button>
          <span>
            Страница {pageIndex + 1} из {page.totalPages}
          </span>
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex >= page.totalPages - 1 || loading}
            onClick={() => void loadPage(pageIndex + 1, search)}
          >
            →
          </button>
        </div>
      ) : null}
    </section>
  )
}
